import { Component, Inject, OnDestroy } from '@angular/core';
import { ApyreAuthService } from './firebase-services/app-auth.service';
import {
  Observable,
  Subscription,
  combineLatest,
  delay,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  of,
  skip,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
import { connectFunctionsEmulator, Functions } from '@angular/fire/functions';
import { connectFirestoreEmulator, Firestore } from '@angular/fire/firestore';
import { Auth } from '@angular/fire/auth';
import { getRedirectResult, onAuthStateChanged } from 'firebase/auth';
import { connectDatabaseEmulator, Database } from '@angular/fire/database';
import { environment } from '../environments/environment';
import { PwaService } from './shared/services/pwa/pwa.service';
import { WINDOW } from './shared/tokens/window';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnDestroy {
  ready$: Observable<boolean> = of(true);
  unload$: Observable<boolean> = of(false);
  loadingRoute$?: Observable<boolean> = of(true);
  connected$: Observable<boolean> = of(false);
  connectedSub: Subscription | null = null;
  constructor(
    private apyreAuth: ApyreAuthService,
    private router: Router,
    private functions: Functions,
    private firestore: Firestore,
    private auth: Auth,
    private database: Database,
    private pwaService: PwaService,
    @Inject(WINDOW) private window: Window,
  ) {
    // Boot splash is first-paint only. In-app NavigationStart must not replay it.
    // Cancel/Error clear loading so a failed first nav cannot stick the overlay;
    // End/Error latch it off. Cancel does not latch, so a boot redirect can
    // still keep the splash up through the follow-on NavigationStart.
    let bootSplashDone = false;
    this.loadingRoute$ = this.router.events.pipe(
      filter(
        (event) =>
          event instanceof RouteConfigLoadStart ||
          event instanceof NavigationStart ||
          event instanceof NavigationEnd ||
          event instanceof NavigationCancel ||
          event instanceof NavigationError,
      ),
      map((event) => {
        if (
          event instanceof NavigationEnd ||
          event instanceof NavigationError
        ) {
          bootSplashDone = true;
          return false;
        }
        if (event instanceof NavigationCancel) {
          return false;
        }
        return !bootSplashDone;
      }),
      distinctUntilChanged(),
      switchMap((loading) => of(loading).pipe(delay(600))),
      startWith(true),
    );
    this.ready$ = combineLatest([
      this.apyreAuth.authReady$.pipe(startWith(false)),
      this.loadingRoute$,
    ]).pipe(map(([authReady, load]) => !load));
    this.unload$ = this.ready$.pipe(delay(800));
    //REPLACE WINDOW WITH INJECTED OBJECT
    const online$ = fromEvent(this.window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(this.window, 'offline').pipe(map(() => false));
    this.connected$ = merge(online$, offline$).pipe(
      startWith(this.window.navigator.onLine),
    );
    this.connectedSub = this.connected$.pipe(skip(1)).subscribe((connected) => {
      if (!connected || !google) this.reload();
    });
  }

  async ngOnInit() {
    if (environment.env === 'local') {
      // Auth emulator is connected in initializePortalAuth (provideAuth).
      connectFunctionsEmulator(this.functions, 'localhost', 5001);
      connectFirestoreEmulator(this.firestore, 'localhost', 8080);
      connectDatabaseEmulator(this.database, 'localhost', 9000);
    }
    getRedirectResult(this.auth)
      .then((r) => console.log('redirect result', r))
      .catch((e) => console.error('redirect error', e));

    onAuthStateChanged(this.auth, (u) => console.log('auth state', u));

    if (environment.env === 'production') {
      this.pwaService.checkForUpdates();
    }
  }

  reload() {
    this.window.location.reload();
  }

  ngOnDestroy(): void {
    if (this.connectedSub) this.connectedSub.unsubscribe();
  }
}
